import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomTool } from './customTool';
import { formatNumber } from '../../../core/formatter/formatNumber';
import * as React from 'react';
import { SectionStyle } from '../../../helpers/SectionStyle';
import { WSpinner } from '../../../core/elements/spinner/WSpinner';

interface LineDiagramProps extends React.ClassAttributes<any> {
    createNewTick?: boolean;
    data: any[];
    legendTitle: any[];
    unit: string[];
    visibleData: any[];
    containerSize?: any;
    hideLegend?: boolean;
    id?: string;
    lineNoDot?: boolean;
    lineColor?: string[];
    lineWidth?: number[];
    legendProps?: any;
    lineChartProps?: any;
    referenceLines?: any[];
    textLines?: any[];
    singleYAxis?: boolean;
    X_dataKey?: string;
    X_padding?: any;
    xAxisProps?: any[];
    yAxisProps?: any[];
    bgColor?: string;
    tooltipLabelPrefix?: string;
    tooltipLabelSuffix?: string;
    customTickFormatter?(idx: number, value: any): string;
    setTitle?: (elem?: JSX.Element) => JSX.Element;
}

interface LineDiagramState extends React.ClassAttributes<any> {
    maxWert: any;
    minWert: any;
    tickArray: any;
}

export class LineDiagram extends React.Component<LineDiagramProps, LineDiagramState> {

    constructor(props: any) {
        super(props);
        this.state = {
            maxWert: null,
            minWert: null,
            tickArray: null
        };
        this.createData = this.createData.bind(this);
        this.legendFormatter = this.legendFormatter.bind(this);
    }

    componentDidMount(): void {
        this.createData(this.props.data);
    }

    componentDidUpdate(prevProps: any): void {
        if (
            this.props.createNewTick
            && (
                (this.props.visibleData || []).length !== (prevProps.visibleData || []).length
                || (this.props.data || []).length !== (prevProps.data || []).length
                || (this.props.referenceLines || []).length !== (prevProps.referenceLines || []).length
            )
        ) {
            this.createData(this.props.data);
        }
    }

    createData(data: any[]) {
        const { visibleData } = this.props;
        const has2YAxis = this.has2YAxis();
        const dataArray: any = {};
        const refValues: any = this.getReferenceLineMinMaxY() || {};
        const refMax: number = refValues.max;
        const refMin: number = refValues.min;

        data.forEach((row: any) => {
            (visibleData || []).forEach((key: string, idx: number) => {
                if (typeof dataArray[key] === 'undefined') {
                    dataArray[key] = [];
                }
                if (row[key] === null) {
                    return;
                }
                dataArray[key].push(row[key] || 0);
            });
        });
        const maxWert: any = {};
        const minWert: any = {};
        const tickArray: any = {};
        const keys: string[] = Object.keys(dataArray);

        // console.log(data, dataArray);

        keys.forEach((k: any, idx: number) => {
            let dataK: any[] = [];
            if (has2YAxis) {
                // Bei 2 Y-Axise berechnen wir Ticks separat.
                dataK = [...dataArray[k]];
            } else {
                // Bei einem Y-Axis berechnen wir Ticks zusammen.
                Object.keys(dataArray).forEach((k: string) => {
                    if (Array.isArray(dataArray[k])) {
                        dataK = [...dataK, ...dataArray[k]];
                    }
                });
            }
            dataK.sort();
            if (dataK.length === 1) {
                // Wenn es nur einen Wert gibt, muss eine 0 in dem Array ergänzt werden, um den Abstand der Skala zu berechnen.
                dataK.push(0);
            }

            // console.log(refMax, refMin, dataK);
            maxWert[k] = typeof refMax === 'number' ? Math.max(...dataK, refMax) : Math.max(...dataK);
            minWert[k] = typeof refMin === 'number' ? Math.min(...dataK, refMin) : Math.min(...dataK);
            if (minWert[k] === maxWert[k]) {
                // Wenn minWert = maxWert, entsteht log(0) ERROR beim Rechnen expo
                minWert[k] = 0;
            }

            const expo = Math.round(Math.log((maxWert[k] - minWert[k]) / 2) / Math.log(10)) - 1;
            const divi = Math.pow(10, expo);
            const minR = divi === 0 ? 0 : Math.round(minWert[k] / divi) * divi;
            let tickNum = 5;
            if (minR > 0) {
                // siehe unten *1.
                tickNum--;
            }

            // const step = (maxWert[k] - minWert[k]) / tickNum;
            // const stepR = divi === 0 ? 0 : Math.round(step / divi) * divi;
            // Anpassung am 17.08.2023, so sieht es schöner aus.
            const step = (maxWert[k] - minWert[k]) / tickNum * (expo > 1 ? 1 : 1.1);
            const stepR = divi === 0 ? 0 : +(step / divi).toFixed(expo > 1 ? 1 : 0) * divi;
            // console.log(k, expo, divi, step, stepR, minR);

            if (!tickArray[k]) {
                tickArray[k] = [];
            }
            if (minR > 0) {
                // *1. Wenn die Y-Achse nicht mit 0 beginnt, sollte der niedrigste Wert nicht auf der X-Achse liegen.
                tickArray[k].push(Math.max(minR - stepR, 0));
            }
            for (let i = 0; i <= tickNum + 1; i++) {
                const tick = minR + i * stepR;
                tickArray[k].push(tick);
            }
        });

        // console.log(minWert, maxWert, tickArray);
        this.setState({ minWert, maxWert, tickArray });
    }

    getDomains(): any {
        const domains: number[][] = [[null, null], [null, null]];
        const visibleData: any[] = this.props.visibleData || [];
        const has2YAxis = this.has2YAxis();
        let keyTicksMax: string = null;
        let maxValue: number = null;

        visibleData.forEach((key: string, idx: number) => {
            const min: number = this.state.minWert[key];
            const max: number = this.state.maxWert[key];
            // console.log(min, max, key, idx);
            if (has2YAxis && idx === 1) {
                domains[idx] = [min, max];
            } else {
                const maxWert: number = (this.state.maxWert || {})[key];
                if (!idx || (typeof maxWert === 'number' && maxWert > maxValue)) {
                    maxValue = maxWert;
                    keyTicksMax = key;
                }
                domains[0] = [
                    idx ? Math.min(domains[0][0], min) : min,
                    idx ? Math.max(domains[0][1], max) : max
                ];
            }
        });

        domains[0][1] = typeof domains[0][1] === 'number' ? domains[0][1] || 1 : domains[0][1];
        domains[1][1] = typeof domains[1][1] === 'number' ? domains[1][1] || 1 : domains[1][1];

        // console.log(domains, keyTicksMax);
        return { domains, keyTicks: keyTicksMax };
    }

    getReferenceLineMinMaxY(): { max: number, min: number } {
        let max: number = null;
        let min: number = null;

        (this.props.referenceLines || []).forEach((line: any) => {
            line = line || {};

            if ((line.ifOverflow || '').toString() !== 'extendDomain' || typeof line.y !== 'number') {
                return;
            }
            max = line.y > max ? line.y : max === null ? line.y : max;
            min = line.y < min ? line.y : min === null ? line.y : min;
        });

        return { max, min };
    }

    legendFormatter(value: any, entry: any): JSX.Element {
        const valueElement = ((e: any) => e === value);
        const idx: number = this.props.visibleData.findIndex(valueElement);

        return <span>{this.props.legendTitle[idx]}</span>;
    }

    has2YAxis(): boolean {
        const { singleYAxis, visibleData } = this.props;
        const has2YAxis: boolean = visibleData.length > 1 && !singleYAxis;
        return has2YAxis;
    }

    render(): JSX.Element {
        const { containerSize, data, lineColor, lineNoDot, lineWidth, singleYAxis, visibleData, X_dataKey, X_padding } = this.props;
        const { tickArray, minWert, maxWert } = this.state;

        if (!tickArray && !minWert && !maxWert) {
            return <WSpinner />;
        }
        const containerId: string = typeof SectionStyle.curPrintSectionId === 'number' ? SectionStyle.getCurrentClass(true) : null;
        const Section: any = new SectionStyle(containerId);
        const backgroundColorSectionChart: string = Section.getPropertyValue('--color-section-chart-background');
        const width: number | string = containerSize && containerSize.width ? containerSize.width : 600;
        const height: number | string = containerSize && containerSize.height ? containerSize.height : '100%';
        const { domains, keyTicks } = this.getDomains();
        const has2YAxis: boolean = this.has2YAxis();
        const legendProps: any = this.props.legendProps || {};
        const lineChartProps: any = this.props.lineChartProps || {};
        const xAxisProps: any = (this.props.xAxisProps || [])[0] || {};
        const yAxisProps: any[] = this.props.yAxisProps || [];
        yAxisProps[0] = yAxisProps[0] || {};
        yAxisProps[1] = yAxisProps[1] || {};
        return <>
            {this.props.setTitle && this.props.setTitle()}
            <ResponsiveContainer width={width} height={height} id={this.props.id}>
                <LineChart
                    style={{ display: 'flex', flexDirection: 'column-reverse', ...(lineChartProps.style || {}) }}
                    data={data}
                    margin={lineChartProps.margin}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke='#555'
                        fill={this.props.bgColor ? this.props.bgColor : backgroundColorSectionChart || 'transparent'}
                    />
                    <XAxis
                        dataKey={X_dataKey ? X_dataKey : 'jahr'}
                        padding={X_padding ? X_padding : { left: 30, right: 30 }}
                        label={xAxisProps.label}
                    />
                    <YAxis
                        yAxisId='left'
                        type='number'
                        tickCount={yAxisProps[0].tickCount}
                        ticks={typeof yAxisProps[0].ticks === 'undefined' ? tickArray[keyTicks] : yAxisProps[0].ticks || undefined}
                        tickLine={true}
                        stroke={has2YAxis && lineColor && lineColor[0] ? lineColor[0] : '#555'}
                        domain={typeof yAxisProps[0].domain === 'undefined' ? domains[0] : yAxisProps[0].domain || undefined}
                        tickFormatter={(value: any) => this.tickFormatter(0, value)}
                        width={yAxisProps[0].width || 100}
                        padding={yAxisProps[0].padding}
                        label={yAxisProps[0].label}
                    />
                    <Line
                        yAxisId='left'
                        type='linear'
                        dataKey={visibleData[0]}
                        stroke={lineColor && lineColor[0] ? lineColor[0] : '#4f81bd'}
                        strokeWidth={lineWidth && lineWidth[0] ? lineWidth[0] : 5}
                        dot={{ stroke: '#4f81bd', strokeWidth: lineNoDot ? 1 : 2, r: lineNoDot ? 0 : 5 }}
                        isAnimationActive={false}
                    />
                    {has2YAxis ?
                        <YAxis
                            yAxisId='right'
                            orientation='right'
                            type='number'
                            tickCount={yAxisProps[1].tickCount}
                            ticks={typeof yAxisProps[1].ticks === 'undefined'
                                ? tickArray[visibleData[1]]
                                : yAxisProps[1].ticks || undefined
                            }
                            tickLine={true}
                            stroke={lineColor && lineColor[1] ? lineColor[1] : '#555'}
                            domain={typeof yAxisProps[1].domain === 'undefined' ? domains[1] : yAxisProps[1].domain || undefined}
                            tickFormatter={(value: any) => this.tickFormatter(1, value)}
                            width={yAxisProps[1].width || 100}
                            padding={yAxisProps[1].padding}
                            label={yAxisProps[1].label}
                        /> : <YAxis yAxisId='right' orientation='right' width={yAxisProps[1].width} />
                    }
                    {visibleData.length > 1 && visibleData.map((d: string, idx: number) => {
                        if (idx === 0) {
                            return null;
                        }
                        return (
                            <Line
                                key={`typ-${idx}`}
                                yAxisId={singleYAxis ? 'left' : 'right'}
                                type='linear'
                                dataKey={d}
                                strokeWidth={lineWidth && lineWidth[idx] ? lineWidth[idx] : 5}
                                stroke={lineColor && lineColor[idx] ? lineColor[idx] : '#888'}
                                dot={{ stroke: '#888', strokeWidth: lineNoDot ? 1 : 2, r: lineNoDot ? 0 : 5 }}
                                isAnimationActive={false}
                            />
                        );
                    })}
                    <Tooltip
                        content={((contentData: any) =>
                            <CustomTool
                                data={contentData}
                                dataKeys={visibleData}
                                labelPrefix={this.props.tooltipLabelPrefix}
                                labelSuffix={this.props.tooltipLabelSuffix}
                                legendTitle={this.props.legendTitle}
                                unit={this.props.unit}
                            />
                        )}
                        wrapperStyle={{
                            color: '#888',
                            backgroundColor: 'whitesmoke',
                            border: '1px solid #ccc',
                            padding: 5,
                            opacity: 0.9
                        }}
                    />
                    {!this.props.hideLegend &&
                        <Legend
                            formatter={this.legendFormatter}
                            verticalAlign='top'
                            align='center'
                            wrapperStyle={{ top: 0, position: 'relative', color: '#555', ...(legendProps.wrapperStyle || {}) }}
                        />
                    }
                    {this.renderReferenceLines()}
                    {this.renderTextLines()}
                </LineChart>
            </ResponsiveContainer>
        </>;
    }

    renderReferenceLines(): JSX.Element[] {
        return (this.props.referenceLines || []).map((line: any, idx: number) => {
            line = line || {};

            if (typeof line.x === 'undefined' && typeof line.y === 'undefined') {
                return null;
            }

            return (
                <ReferenceLine
                    key={`line-${idx}`}
                    yAxisId='left'
                    x={line.x}
                    y={line.y}
                    ifOverflow={line.ifOverflow}
                    label={line.label}
                    stroke={line.stroke}
                    strokeWidth={line.strokeWidth}
                />
            );
        });
    }

    renderTextLines(): JSX.Element[] {
        return (this.props.textLines || []).map((text: any, idx: number) => {
            text = text || {};

            if (typeof text.value === 'undefined') {
                return null;
            }

            return (
                <text
                    key={`text-${idx}`}
                    x={text.x}
                    dx={text.dx}
                    y={text.y}
                    dy={text.dy}
                    style={text.style}
                    fill={text.fill}
                    textAnchor={text.textAnchor}
                >
                    {text.value}
                </text>
            );
        });
    }

    tickFormatter(idx: number, value: any): string {
        const { unit, customTickFormatter } = this.props;
        if (typeof customTickFormatter === 'function') {
            return customTickFormatter(idx, value);
        } else {
            if (unit[idx] === '%' && value > 100) {
                // max. 100%
                return '';
            }
            return formatNumber(value);
        }
    }

}
